/**
 * Per-clip BLUR/ERASE regions (Erase tool) — censor patches (gaussian blur or pixelate
 * mosaic) over parts of a media clip — shared by the live preview (`webav-canvas`) and the
 * export worker (`worker-compositor`) so a censored clip looks identical in the editor and
 * the exported MP4. Pure canvas (ctx.filter blur is GPU-accelerated with a built-in CPU
 * fallback; pixelate is a down/up-scale blit) — NO backend, NO shader required.
 *
 * How a region is applied (mirrors the chroma-key scratch pattern):
 *   1. Copy the CURRENT composite (the just-drawn frame) into a device-space scratch,
 *      blurred (ctx.filter) or pixelated (downscale → upscale with smoothing off).
 *   2. Keep only the region shape via `destination-in`, traced in the caller's transformed
 *      space (so it follows the clip's position/rotation/scale); a blurred white fill
 *      gives the feathered soft edge.
 *   3. Composite the censored patch back over the frame in device space at full alpha.
 * Geometry is normalized 0..1 within the frame rect (fx,fy,fw,fh), like the Mask tool.
 */

type AnyCtx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export interface BlurRegionParams {
  shape?: 'rectangle' | 'ellipse';
  /** Center + size, normalized 0..1 of the frame. Defaults to a centered 30% box. */
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  /** 'blur' = gaussian censor (default), 'pixelate' = mosaic blocks. */
  mode?: 'blur' | 'pixelate';
  /** 0..1 intensity. Default 0.5. */
  strength?: number;
  /** 0..1 soft edge. Default 0.15. */
  feather?: number;
  /** Optional serializable path for a blur-region Motion Tracking attachment. */
  motionTracking?: BlurRegionMotionTrackingParams | null;
}

export interface BlurRegionMotionTrackingParams {
  follow: { position: boolean; scale: boolean; rotation: boolean };
  samples: Array<{ tSeconds: number; centerX: number; centerY: number; scale: number; rotation: number; confidence: number }>;
  corrections?: Array<{ tSeconds: number; x: number; y: number; scale: number; rotation: number }>;
  attachmentOffset: { x: number; y: number; scale: number; rotation: number };
  baseWidth: number;
  baseHeight: number;
}

/**
 * Scratch factory owned by each side (preview = document canvases, worker = OffscreenCanvas),
 * mirroring how the chroma-key scratch is provided. 'full' is canvas-sized (the censored
 * copy); 'small' is the tiny intermediate for the pixelate down/up-scale.
 */
export type BlurScratchProvider = (width: number, height: number, key: 'full' | 'small') => AnyCtx | null;

function clamp(v: number, lo: number, hi: number): number { return v < lo ? lo : v > hi ? hi : v; }

/** True when the clip actually carries a censor region — lets the renderers keep their fast path. */
export function blurRegionsActive(regions: readonly BlurRegionParams[] | null | undefined): boolean {
  return Array.isArray(regions) && regions.length > 0;
}

/**
 * Apply every censor region over the frame rect (fx,fy,fw,fh — the caller's transformed
 * space; the caller's current transform is used to trace the shapes). Call AFTER the media
 * frame is drawn and AFTER ctx.filter is reset, BEFORE the temp/vignette overlays — the
 * SAME point in both compositors. `refScale` = outputWidth / 1080 (blur px reference).
 */
export function applyBlurRegions(
  ctx: AnyCtx,
  regions: readonly BlurRegionParams[],
  fx: number, fy: number, fw: number, fh: number,
  refScale: number,
  scratch2d: BlurScratchProvider,
  clipRelativeTimeSeconds = 0,
): void {
  const canvasW = ctx.canvas.width;
  const canvasH = ctx.canvas.height;
  if (!canvasW || !canvasH) return;

  for (const rawRegion of regions) {
    const region = resolveTrackedBlurRegion(rawRegion, clipRelativeTimeSeconds);
    const full = scratch2d(canvasW, canvasH, 'full');
    if (!full) return; // scratch unavailable → draw un-censored rather than nothing
    const strength = clamp(region.strength ?? 0.5, 0, 1);
    const mode = region.mode === 'pixelate' ? 'pixelate' : 'blur';

    // 1) Censored copy of the current composite, device space (1:1).
    full.save();
    full.setTransform(1, 0, 0, 1, 0, 0);
    full.clearRect(0, 0, canvasW, canvasH);
    let copied = false;
    if (mode === 'pixelate') {
      // Mosaic: block size at the 1080 reference, scaled to the output.
      const block = Math.max(2, (4 + 60 * strength) * refScale);
      const sw = Math.max(1, Math.round(canvasW / block));
      const sh = Math.max(1, Math.round(canvasH / block));
      const small = scratch2d(sw, sh, 'small');
      if (small) {
        small.save();
        small.setTransform(1, 0, 0, 1, 0, 0);
        small.imageSmoothingEnabled = true;
        small.clearRect(0, 0, sw, sh);
        small.drawImage(ctx.canvas, 0, 0, canvasW, canvasH, 0, 0, sw, sh);
        small.restore();
        full.imageSmoothingEnabled = false;
        full.drawImage(small.canvas, 0, 0, sw, sh, 0, 0, canvasW, canvasH);
        full.imageSmoothingEnabled = true;
        copied = true;
      }
    }
    if (!copied) {
      const px = (2 + 38 * strength) * refScale;
      full.filter = `blur(${px.toFixed(2)}px)`;
      full.drawImage(ctx.canvas, 0, 0);
      full.filter = 'none';
    }

    // 2) Keep only the region shape — destination-in, traced with the caller's transform so
    //    the patch follows the clip; a feather-blurred white fill soft-edges the alpha.
    full.globalCompositeOperation = 'destination-in';
    const featherPx = clamp(region.feather ?? 0.15, 0, 1) * 40 * refScale;
    if (featherPx > 0.5) full.filter = `blur(${featherPx.toFixed(2)}px)`;
    full.setTransform(ctx.getTransform());
    const cx = fx + (region.x ?? 0.5) * fw;
    const cy = fy + (region.y ?? 0.5) * fh;
    const w = Math.max(0.01, region.width ?? 0.3) * fw;
    const h = Math.max(0.01, region.height ?? 0.3) * fh;
    full.beginPath();
    if ((region.shape ?? 'rectangle') === 'ellipse') {
      full.ellipse(cx, cy, w / 2, h / 2, 0, 0, Math.PI * 2);
    } else {
      full.rect(cx - w / 2, cy - h / 2, w, h);
    }
    full.fillStyle = '#ffffff';
    full.fill();
    full.restore(); // clears composite op, filter and transform

    // 3) Composite the censored patch back, device space, full alpha (the copy already
    //    carries the composited look — the clip's own opacity must not thin the censor).
    //    Deliberately NOT save/restore: the caller's crop/mask clip paths stay in effect,
    //    so a censor never paints outside the visible media (same both sides).
    const prevTransform = ctx.getTransform();
    const prevAlpha = ctx.globalAlpha;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.drawImage(full.canvas, 0, 0);
    ctx.globalAlpha = prevAlpha;
    ctx.setTransform(prevTransform);
  }
}

/** Shared preview/export geometry resolver for one tracked censor region. */
export function resolveTrackedBlurRegion(region: BlurRegionParams, clipRelativeTimeSeconds: number): BlurRegionParams {
  const tracking = region.motionTracking;
  if (!tracking?.samples?.length) return region;
  const pose = interpolatePose(tracking.samples, clipRelativeTimeSeconds);
  const correction = interpolateCorrection(tracking.corrections ?? [], clipRelativeTimeSeconds);
  const ratio = Math.max(0.01, tracking.attachmentOffset.scale * pose.scale * correction.scale);
  return {
    ...region,
    x: tracking.follow.position
      ? clamp(pose.centerX + tracking.attachmentOffset.x + correction.x / 100, 0, 1)
      : region.x,
    y: tracking.follow.position
      ? clamp(pose.centerY + tracking.attachmentOffset.y + correction.y / 100, 0, 1)
      : region.y,
    width: tracking.follow.scale ? clamp(tracking.baseWidth * ratio, 0.01, 1) : region.width,
    height: tracking.follow.scale ? clamp(tracking.baseHeight * ratio, 0.01, 1) : region.height,
    motionTracking: null,
  };
}

function interpolatePose(
  samples: BlurRegionMotionTrackingParams['samples'],
  time: number,
): BlurRegionMotionTrackingParams['samples'][number] {
  if (samples.length === 1 || time <= samples[0]!.tSeconds) return samples[0]!;
  const last = samples[samples.length - 1]!;
  if (time >= last.tSeconds) return last;
  const rightIndex = samples.findIndex(sample => sample.tSeconds >= time);
  const right = samples[Math.max(1, rightIndex)]!;
  const left = samples[Math.max(0, rightIndex - 1)]!;
  const ratio = clamp((time - left.tSeconds) / Math.max(0.0001, right.tSeconds - left.tSeconds), 0, 1);
  return {
    tSeconds: time,
    centerX: lerp(left.centerX, right.centerX, ratio),
    centerY: lerp(left.centerY, right.centerY, ratio),
    scale: lerp(left.scale, right.scale, ratio),
    rotation: lerp(left.rotation, right.rotation, ratio),
    confidence: lerp(left.confidence, right.confidence, ratio),
  };
}

function interpolateCorrection(
  points: NonNullable<BlurRegionMotionTrackingParams['corrections']>,
  time: number,
): { x: number; y: number; scale: number; rotation: number } {
  if (!points.length) return { x: 0, y: 0, scale: 1, rotation: 0 };
  if (points.length === 1 || time <= points[0]!.tSeconds) return points[0]!;
  const last = points[points.length - 1]!;
  if (time >= last.tSeconds) return last;
  const rightIndex = points.findIndex(point => point.tSeconds >= time);
  const right = points[Math.max(1, rightIndex)]!;
  const left = points[Math.max(0, rightIndex - 1)]!;
  const ratio = clamp((time - left.tSeconds) / Math.max(0.0001, right.tSeconds - left.tSeconds), 0, 1);
  return {
    x: lerp(left.x, right.x, ratio),
    y: lerp(left.y, right.y, ratio),
    scale: lerp(left.scale, right.scale, ratio),
    rotation: lerp(left.rotation, right.rotation, ratio),
  };
}

function lerp(left: number, right: number, ratio: number): number { return left + (right - left) * ratio; }
